import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './Checkins.css'

// ── Column definitions ────────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'name',       label: 'Name',             defaultVisible: true,  defaultWidth: 160 },
  { key: 'company',    label: 'Unternehmen',       defaultVisible: true,  defaultWidth: 130 },
  // The tenant's own order number, from whichever field they flagged as the reference
  // in the Flow Builder. Off by default for the same reason as Ziel below: a site that
  // flags no field has nothing to put in it.
  { key: 'reference',  label: 'Referenznummer',    defaultVisible: false, defaultWidth: 140 },
  { key: 'flow_mode',  label: 'Prozess',           defaultVisible: true,  defaultWidth: 110 },
  { key: 'reason',     label: 'Besuchsgrund',      defaultVisible: true,  defaultWidth: 150 },
  { key: 'freight',    label: 'Frachtbrief-Nr.',   defaultVisible: false, defaultWidth: 130 },
  { key: 'contact',    label: 'Ansprechpartner',   defaultVisible: false, defaultWidth: 150 },
  { key: 'email',      label: 'E-Mail',            defaultVisible: true,  defaultWidth: 180 },
  { key: 'phone',      label: 'Telefon',           defaultVisible: false, defaultWidth: 130 },
  { key: 'license',    label: 'Kennzeichen',       defaultVisible: false, defaultWidth: 110 },
  { key: 'visit_date', label: 'Besuchsdatum',      defaultVisible: true,  defaultWidth: 110 },
  { key: 'checkin',    label: 'Check-in',          defaultVisible: true,  defaultWidth: 145 },
  { key: 'checkout',   label: 'Check-out',         defaultVisible: true,  defaultWidth: 145 },
  // Where this visitor was sent, and what they were shown. Off by default: only tenants
  // using destinations or dynamic info pages have anything in them, and an empty column
  // in everybody else's list is worse than one they switch on.
  { key: 'destination', label: 'Ziel',             defaultVisible: false, defaultWidth: 130 },
  { key: 'shown_page',  label: 'Angezeigte Seite', defaultVisible: false, defaultWidth: 160 },
  { key: 'rules',      label: 'Regeln gelesen',    defaultVisible: false, defaultWidth: 145 },
  { key: 'quiz',       label: 'Quiz',              defaultVisible: false, defaultWidth: 145 },
  { key: 'pdf',        label: 'PDF',               defaultVisible: true,  defaultWidth: 80  },
  { key: 'document',   label: 'Dokument',          defaultVisible: true,  defaultWidth: 100 },
  { key: 'qr',         label: 'QR-Code',           defaultVisible: false, defaultWidth: 100 },
  { key: 'sms',        label: 'SMS',               defaultVisible: false, defaultWidth: 90  },
  { key: 'status',     label: 'Status',            defaultVisible: true,  defaultWidth: 110 },
]

// How the shown page was chosen, in admin-facing German.
const SHOWN_SOURCE_LABELS = {
  rule:     'Regel',
  manual:   'Zuweisung',
  fallback: 'Rückfallseite',
}

const LS_VISIBLE = 'checkins-col-visible'
const LS_WIDTHS  = 'checkins-col-widths'

function loadVisible() {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_VISIBLE) || '{}')
    return Object.fromEntries(COLUMNS.map(c => [c.key, saved[c.key] ?? c.defaultVisible]))
  } catch { return Object.fromEntries(COLUMNS.map(c => [c.key, c.defaultVisible])) }
}

function loadWidths() {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_WIDTHS) || '{}')
    return Object.fromEntries(COLUMNS.map(c => [c.key, saved[c.key] ?? c.defaultWidth]))
  } catch { return Object.fromEntries(COLUMNS.map(c => [c.key, c.defaultWidth])) }
}

// ── Component ─────────────────────────────────────────────────────────────────
const Checkins = () => {
  const { apiFetch, admin, isOwner, features } = useAuth()

  const [tenants,         setTenants]         = useState([])
  const [checkins,        setCheckins]        = useState([])
  const [loading,         setLoading]         = useState(false)
  const [error,           setError]           = useState(null)
  const [selectedTenant,  setSelectedTenant]  = useState('')
  const [filterDate,      setFilterDate]      = useState('')
  const [downloading,     setDownloading]     = useState(null)
  const [viewingDocument, setViewingDocument] = useState(null)
  const [lastUpdated,     setLastUpdated]     = useState(null)
  const [autoRefresh,     setAutoRefresh]     = useState(true)
  const [pickerOpen,      setPickerOpen]      = useState(false)
  const [colVisible,      setColVisible]      = useState(loadVisible)
  const [colWidths,       setColWidths]       = useState(loadWidths)
  const [filterText,      setFilterText]      = useState('')
  const [sortKey,         setSortKey]         = useState('checkin')
  const [sortDir,         setSortDir]         = useState('desc')
  const [qrModal,         setQrModal]         = useState(null) // { qrBase64, visitorName }
  const [generatingQr,    setGeneratingQr]    = useState(null) // sessionId
  const [tenantFeatures,  setTenantFeatures]  = useState(null)
  const [smsModal,        setSmsModal]        = useState(null) // { sessionId, visitorName, phone }
  const [smsMessage,      setSmsMessage]      = useState('')
  const [smsSending,      setSmsSending]      = useState(false)
  const [smsResult,       setSmsResult]       = useState(null) // { ok, text }
  const [pendingCheckins, setPendingCheckins] = useState([])
  const [approvingId,     setApprovingId]     = useState(null)
  const [rejectModal,     setRejectModal]     = useState(null) // { sessionId, visitorName }
  const [rejectReason,    setRejectReason]    = useState('')
  const [rejectSaving,    setRejectSaving]    = useState(false)

  const pickerRef  = useRef(null)
  const resizeRef  = useRef(null) // { key, startX, startWidth }

  // Persist visibility changes
  useEffect(() => {
    localStorage.setItem(LS_VISIBLE, JSON.stringify(colVisible))
  }, [colVisible])

  // Persist width changes
  useEffect(() => {
    localStorage.setItem(LS_WIDTHS, JSON.stringify(colWidths))
  }, [colWidths])

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [pickerOpen])

  // ── Column resize ───────────────────────────────────────────────────────────
  const onResizeMove = useCallback((e) => {
    if (!resizeRef.current) return
    const { key, startX, startWidth } = resizeRef.current
    const next = Math.max(50, startWidth + (e.clientX - startX))
    setColWidths(prev => ({ ...prev, [key]: next }))
  }, [])

  const onResizeEnd = useCallback(() => {
    resizeRef.current = null
    document.removeEventListener('mousemove', onResizeMove)
    document.removeEventListener('mouseup', onResizeEnd)
  }, [onResizeMove])

  const onResizeStart = useCallback((e, key) => {
    e.preventDefault()
    resizeRef.current = { key, startX: e.clientX, startWidth: colWidths[key] }
    document.addEventListener('mousemove', onResizeMove)
    document.addEventListener('mouseup', onResizeEnd)
  }, [colWidths, onResizeMove, onResizeEnd])

  // ── Load features for selected tenant ──────────────────────────────────────
  // Owner accounts have no tenant_id so AuthContext features is null.
  // Fetch the selected tenant's features fresh whenever the tenant changes.
  useEffect(() => {
    if (!isOwner || !selectedTenant) return
    apiFetch(`/api/admin/tenant-features/${selectedTenant}`)
      .then(r => r.json())
      .then(data => setTenantFeatures(data.features || {}))
      .catch(() => {})
  }, [isOwner, selectedTenant])

  // Effective features: per-tenant fetch for owner, AuthContext for tenant admins
  const effectiveFeatures = isOwner ? (tenantFeatures ?? {}) : (features ?? {})

  // ── Data loading ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants')
        .then(r => r.json())
        .then(data => {
          setTenants(data)
          if (data.length > 0) setSelectedTenant(data[0].id)
        })
        .catch(() => setError('Mandanten konnten nicht geladen werden'))
    } else {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner])

  const fetchPending = useCallback(() => {
    if (!selectedTenant) return
    apiFetch(`/api/admin/checkins/${selectedTenant}/pending`)
      .then(r => r.json())
      .then(data => Array.isArray(data) ? setPendingCheckins(data) : setPendingCheckins([]))
      .catch(() => {})
  }, [selectedTenant])

  const fetchCheckins = useCallback(() => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (filterDate) params.set('date', filterDate)
    Promise.all([
      apiFetch(`/api/admin/checkins/${selectedTenant}?${params}`).then(r => r.json()),
      apiFetch(`/api/admin/checkins/${selectedTenant}/pending`).then(r => r.json()),
    ])
      .then(([checkinsData, pendingData]) => {
        if (checkinsData.error) throw new Error(checkinsData.error)
        setCheckins(checkinsData)
        setPendingCheckins(Array.isArray(pendingData) ? pendingData : [])
        setLastUpdated(new Date())
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [selectedTenant, filterDate])

  useEffect(() => { fetchCheckins() }, [fetchCheckins])

  useEffect(() => {
    if (!autoRefresh || !selectedTenant) return
    const interval = setInterval(fetchCheckins, 30000)
    return () => clearInterval(interval)
  }, [autoRefresh, fetchCheckins])

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleGenerateQr = async (sessionId) => {
    setGeneratingQr(sessionId)
    try {
      const res  = await apiFetch(`/api/admin/checkins/${sessionId}/generate-qr`)
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Fehler')
      setQrModal({ qrBase64: data.qrBase64, visitorName: data.visitorName, qrString: data.qrString, visitDate: data.visitDate, exitLabel: data.exitLabel, exitNote: data.exitNote })
    } catch (err) {
      alert(err.message)
    } finally {
      setGeneratingQr(null)
    }
  }

  const handlePrintQr = ({ qrBase64, visitorName, visitDate, exitLabel, exitNote }) => {
    const win = window.open('', '_blank', 'width=400,height=500')
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>QR-Code – ${visitorName}</title>
  <style>
    body { font-family: Arial, sans-serif; display: flex; flex-direction: column;
           align-items: center; justify-content: center; min-height: 100vh;
           margin: 0; padding: 24px; box-sizing: border-box; text-align: center; }
    h2  { margin: 0 0 4px; font-size: 20px; }
    p   { margin: 4px 0; color: #555; font-size: 14px; }
    img { width: 220px; height: 220px; margin: 16px 0; display: block; }
    .label { font-size: 12px; color: #888; margin-top: 12px; }
    @media print { body { min-height: auto; padding: 8px; } }
  </style>
</head>
<body>
  <p class="label">${exitLabel}</p>
  <h2>${visitorName}</h2>
  ${visitDate ? `<p>${visitDate}</p>` : ''}
  <img src="${qrBase64}" alt="QR Code" />
  <p class="label">${exitNote}</p>
  <script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); }<\/script>
</body>
</html>`)
    win.document.close()
  }

  const handleDownloadPdf = async (sessionId, visitorName, visitDate) => {
    setDownloading(sessionId)
    try {
      const res = await apiFetch(`/api/admin/checkins/${sessionId}/pdf`)
      if (!res.ok) throw new Error('PDF nicht verfügbar')
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `Sicherheitsregeln-${visitDate}-${visitorName.replace(/\s+/g, '-')}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(`PDF-Download fehlgeschlagen: ${err.message}`)
    } finally {
      setDownloading(null)
    }
  }

  const handleViewDocument = async (sessionId, visitorName) => {
    try {
      const res = await apiFetch(`/api/admin/checkins/${sessionId}/document`)
      if (!res.ok) throw new Error('Dokument nicht verfügbar')
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      // Document scans are now PDFs; open in a new tab instead of the image lightbox
      if (blob.type === 'application/pdf') {
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 10_000)
      } else {
        setViewingDocument({ url, name: `${visitorName}-Dokument.jpg` })
      }
    } catch (err) {
      setError(`Dokument-Download fehlgeschlagen: ${err.message}`)
    }
  }

  const closeDocument = () => {
    if (viewingDocument) URL.revokeObjectURL(viewingDocument.url)
    setViewingDocument(null)
  }

  const toggleColumn = (key) => {
    setColVisible(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const SMS_PRESETS = [
    'Sie können jetzt das Gelände betreten.',
    'Bitte fahren Sie zu Tor 2.',
    'Bitte warten Sie kurz — wir kommen zu Ihnen.',
    'Ihre Lieferung wurde angenommen. Vielen Dank.',
  ]

  const openSmsModal = (c) => {
    setSmsModal({ sessionId: c.session_id, visitorName: `${c.first_name} ${c.last_name}`, phone: c.phone })
    setSmsMessage('')
    setSmsResult(null)
  }

  const handleApprove = async (sessionId) => {
    setApprovingId(sessionId)
    try {
      const res = await apiFetch(`/api/admin/checkins/${sessionId}/approve`, {
        method: 'POST',
        body:   JSON.stringify({ tenantId: selectedTenant }),
      })
      if (!res.ok) throw new Error()
      setPendingCheckins(prev => prev.filter(c => c.session_id !== sessionId))
      fetchCheckins()
    } catch {
      setError('Genehmigung fehlgeschlagen.')
    } finally {
      setApprovingId(null)
    }
  }

  const handleReject = async () => {
    if (!rejectModal) return
    setRejectSaving(true)
    try {
      const res = await apiFetch(`/api/admin/checkins/${rejectModal.sessionId}/reject`, {
        method: 'POST',
        body:   JSON.stringify({ tenantId: selectedTenant, reason: rejectReason }),
      })
      if (!res.ok) throw new Error()
      setPendingCheckins(prev => prev.filter(c => c.session_id !== rejectModal.sessionId))
      setRejectModal(null)
      setRejectReason('')
      fetchCheckins()
    } catch {
      setError('Ablehnung fehlgeschlagen.')
    } finally {
      setRejectSaving(false)
    }
  }

  const handleSendSms = async () => {
    if (!smsModal || !smsMessage.trim()) return
    setSmsSending(true)
    setSmsResult(null)
    try {
      const res  = await apiFetch(`/api/admin/checkins/${smsModal.sessionId}/send-sms`, {
        method: 'POST',
        body:   JSON.stringify({ message: smsMessage, tenantId: selectedTenant }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Fehler')
      setSmsResult({ ok: true, text: 'SMS gesendet.' })
    } catch (err) {
      setSmsResult({ ok: false, text: err.message })
    } finally {
      setSmsSending(false)
    }
  }

  const formatDate = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  // ── Cell renderer ────────────────────────────────────────────────────────────
  const renderCell = (key, c) => {
    switch (key) {
      case 'name':
        return <strong>{c.first_name} {c.last_name}</strong>
      case 'company':
        return c.company || <span className="checkins-placeholder">—</span>
      case 'flow_mode':
        if (c.checkin_type === 'pre-checkin') {
          return (
            <>
              <span className="badge badge-orange">Pre-Checkin</span>
              {c.flow_mode && <> <span className="badge badge-blue">{c.flow_mode}</span></>}
            </>
          )
        }
        return c.flow_mode
          ? <span className="badge badge-blue">{c.flow_mode}</span>
          : <span className="checkins-placeholder">—</span>
      case 'reference':
        return c.reference || <span className="checkins-placeholder">—</span>
      case 'reason':
        return c.reason_for_visit || <span className="checkins-placeholder">—</span>
      case 'freight':
        return c.freight_number || <span className="checkins-placeholder">—</span>
      case 'contact':
        return c.contact_person || <span className="checkins-placeholder">—</span>
      case 'email':
        return <span className="checkins-email-cell">{c.email}</span>
      case 'phone':
        return c.phone || <span className="checkins-placeholder">—</span>
      case 'license':
        return c.license_plate || <span className="checkins-placeholder">—</span>
      case 'visit_date':
        return c.visit_date
      case 'checkin': {
        if (c.status === 'rejected' || c.status === 'expired') {
          return <span className="checkins-placeholder">—</span>
        }
        if (c.checkin_type === 'pre-checkin') {
          const timeVal = Object.values(c.extra_data || {})
            .find(v => typeof v === 'string' && /^\d{2}:\d{2}$/.test(v))
          return <span className="badge badge-orange">{timeVal ? `${c.visit_date} ${timeVal}` : c.visit_date}</span>
        }
        return <span className="badge badge-blue">{formatDate(c.created_at)}</span>
      }
      case 'checkout':
        return c.checked_out_at
          ? <span className="badge badge-grey">✓ {formatDate(c.checked_out_at)}</span>
          : <span className="checkins-placeholder">—</span>
      case 'destination':
        return c.destination
          ? <span className="badge badge-blue">{c.destination}</span>
          : <span className="checkins-placeholder">—</span>
      case 'shown_page':
        // How it was chosen matters as much as which page: a rule and a person picking
        // it are the first thing to separate when a driver ends up in the wrong place.
        return c.shown_page_name
          ? (
            <span title={c.shown_source ? `ausgewählt per ${SHOWN_SOURCE_LABELS[c.shown_source] ?? c.shown_source}` : ''}>
              {c.shown_page_name}
              {c.shown_source && (
                <span className="checkins-placeholder"> ({SHOWN_SOURCE_LABELS[c.shown_source] ?? c.shown_source})</span>
              )}
            </span>
          )
          : <span className="checkins-placeholder">—</span>
      case 'rules':
        return c.safety_rules_read_at
          ? <span className="badge badge-green">✓ {formatDate(c.safety_rules_read_at)}</span>
          : <span className="badge badge-grey">—</span>
      case 'quiz':
        return c.quiz_completed_at
          ? <span className="badge badge-green">✓ {formatDate(c.quiz_completed_at)}</span>
          : <span className="badge badge-grey">—</span>
      case 'pdf':
        return c.pdf_available
          ? (
            <button
              className="btn btn-ghost checkins-pdf-btn"
              disabled={downloading === c.session_id}
              onClick={() => handleDownloadPdf(c.session_id, `${c.first_name} ${c.last_name}`, c.visit_date)}
            >
              {downloading === c.session_id ? <span className="spinner spinner-xs" /> : '⬇ PDF'}
            </button>
          ) : <span className="checkins-no-pdf">—</span>
      case 'document':
        return c.document_available
          ? (
            <button
              className="btn btn-ghost checkins-pdf-btn"
              onClick={() => handleViewDocument(c.session_id, `${c.first_name} ${c.last_name}`)}
            >
              🪪 Ansehen
            </button>
          ) : <span className="checkins-no-pdf">—</span>
      case 'qr':
        if (!effectiveFeatures?.delivery_qr_generation) return null
        if (!/lieferung|delivery/i.test(c.flow_mode || '')) return <span className="checkins-no-pdf">—</span>
        if (c.checked_out_at) return <span className="checkins-no-pdf">—</span>
        return (
          <button
            className="btn btn-ghost checkins-pdf-btn"
            disabled={generatingQr === c.session_id}
            onClick={() => handleGenerateQr(c.session_id)}
          >
            {generatingQr === c.session_id ? <span className="spinner spinner-xs" /> : '⬛ QR'}
          </button>
        )
      case 'sms':
        if (!c.phone) return <span className="checkins-no-pdf">—</span>
        return (
          <button className="btn btn-ghost checkins-pdf-btn" onClick={() => openSmsModal(c)}>
            💬 SMS
          </button>
        )
      case 'status':
        if (!c.status || c.status === 'approved') return <span className="checkins-placeholder">—</span>
        if (c.status === 'rejected') return <span className="badge badge-red">✕ Abgelehnt</span>
        if (c.status === 'expired')  return <span className="badge badge-grey">Abgelaufen</span>
        if (c.status === 'pending')  return <span className="badge badge-orange">Ausstehend</span>
        return <span className="badge badge-grey">{c.status}</span>
      default:
        return null
    }
  }

  const SORTABLE_COLS = new Set([
    'name','company','reference','flow_mode','reason','freight','contact','email','phone',
    'license','visit_date','checkin','checkout','rules','quiz',
    'destination','shown_page',
  ])

  function handleSort(key) {
    if (!SORTABLE_COLS.has(key)) return
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('asc') }
  }

  const displayCheckins = useMemo(() => {
    let result = [...checkins]
    if (filterText) {
      const q = filterText.toLowerCase()
      result = result.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (c.reference || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.reason_for_visit || '').toLowerCase().includes(q) ||
        (c.freight_number || '').toLowerCase().includes(q) ||
        (c.contact_person || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.license_plate || '').toLowerCase().includes(q) ||
        (c.flow_mode || '').toLowerCase().includes(q)
      )
    }
    // Pending approvals are shown in their own table above — exclude from main list
    result = result.filter(c => c.status !== 'pending')

    result.sort((a, b) => {
      let va, vb
      switch (sortKey) {
        case 'name':     va = `${a.first_name} ${a.last_name}`; vb = `${b.first_name} ${b.last_name}`; break
        case 'company':  va = a.company || ''; vb = b.company || ''; break
        case 'reference':va = a.reference || ''; vb = b.reference || ''; break
        case 'flow_mode':va = a.flow_mode || ''; vb = b.flow_mode || ''; break
        case 'reason':   va = a.reason_for_visit || ''; vb = b.reason_for_visit || ''; break
        case 'freight':  va = a.freight_number || ''; vb = b.freight_number || ''; break
        case 'contact':  va = a.contact_person || ''; vb = b.contact_person || ''; break
        case 'email':    va = a.email || ''; vb = b.email || ''; break
        case 'phone':    va = a.phone || ''; vb = b.phone || ''; break
        case 'license':  va = a.license_plate || ''; vb = b.license_plate || ''; break
        case 'visit_date':va = a.visit_date || ''; vb = b.visit_date || ''; break
        case 'checkin':  va = a.created_at || ''; vb = b.created_at || ''; break
        case 'checkout': va = a.checked_out_at || ''; vb = b.checked_out_at || ''; break
        case 'rules':    va = a.safety_rules_read_at || ''; vb = b.safety_rules_read_at || ''; break
        case 'quiz':     va = a.quiz_completed_at || ''; vb = b.quiz_completed_at || ''; break
        case 'destination': va = a.destination || ''; vb = b.destination || ''; break
        case 'shown_page':  va = a.shown_page_name || ''; vb = b.shown_page_name || ''; break
        default:         va = a.created_at || ''; vb = b.created_at || ''
      }
      const cmp = va.localeCompare(vb, 'de')
      return sortDir === 'asc' ? cmp : -cmp
    })
    return result
  }, [checkins, filterText, sortKey, sortDir])

  const smsFeatureOn = effectiveFeatures.sms_enabled !== false
  const visibleCols = COLUMNS.filter(c => colVisible[c.key] && (c.key !== 'sms' || smsFeatureOn))

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Check-ins</h1>
          <p>Übersicht aller abgeschlossenen Check-ins</p>
        </div>
        <div className="checkins-header-actions">
          {lastUpdated && (
            <span className="checkins-last-updated">
              Aktualisiert: {lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <label className="checkins-auto-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              className="checkins-auto-checkbox"
            />
            Auto (30s)
          </label>
          <button
            className="btn btn-ghost checkins-refresh-btn"
            onClick={fetchCheckins}
            disabled={loading}
          >
            {loading ? <span className="spinner spinner-sm" /> : '↻'} Aktualisieren
          </button>

          {/* Column picker */}
          <div className="checkins-col-picker-wrap" ref={pickerRef}>
            <button
              className="btn btn-ghost checkins-col-picker-btn"
              onClick={() => setPickerOpen(o => !o)}
            >
              Spalten ▾
            </button>
            {pickerOpen && (
              <div className="checkins-col-picker">
                <div className="checkins-col-picker-title">Spalten anzeigen</div>
                {COLUMNS.filter(c => c.key !== 'sms' || smsFeatureOn).map(col => (
                  <label key={col.key} className="checkins-col-picker-item">
                    <input
                      type="checkbox"
                      checked={!!colVisible[col.key]}
                      onChange={() => toggleColumn(col.key)}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card checkins-filters-card">
        <div className="row checkins-filters-row">
          {isOwner && (
            <div className="checkins-filter-tenant">
              <label className="form-group checkins-filter-label">
                <span className="checkins-filter-label-text">Mandant</span>
                <select
                  className="form-control"
                  value={selectedTenant}
                  onChange={e => setSelectedTenant(e.target.value)}
                >
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <div className="checkins-filter-date">
            <label className="form-group checkins-filter-label">
              <span className="checkins-filter-label-text">Datum filtern</span>
              <input
                type="date"
                className="form-control"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
              />
            </label>
          </div>
          {filterDate && (
            <div className="checkins-filter-clear">
              <button className="btn btn-ghost" onClick={() => setFilterDate('')}>
                Datum zurücksetzen
              </button>
            </div>
          )}
          <div className="checkins-search-wrap">
            <input
              type="text"
              className="form-control"
              placeholder="Name, Firma, E-Mail…"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card pending-approvals-card">
        <div className="section-title pending-approvals-title">
          ⏳ Ausstehende Genehmigungen
          {pendingCheckins.length > 0 && (
            <span className="pending-approvals-badge">{pendingCheckins.length}</span>
          )}
        </div>
        {pendingCheckins.length === 0 ? (
          <p className="pending-approvals-empty">Keine ausstehenden Genehmigungen.</p>
        ) : (
          <table className="pending-approvals-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Unternehmen</th>
                <th>Quelle</th>
                <th>Eingegangen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pendingCheckins.map(c => (
                <tr key={c.session_id}>
                  <td><strong>{c.first_name} {c.last_name}</strong></td>
                  <td>{c.company || <span className="checkins-placeholder">—</span>}</td>
                  <td>
                    <span className={`badge ${c.checkin_source === 'kiosk' ? 'badge-blue' : 'badge-orange'}`}>
                      {c.checkin_source === 'kiosk' ? 'Kiosk' : 'Pre-Checkin'}
                    </span>
                  </td>
                  <td>{new Date(c.created_at).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</td>
                  <td className="pending-approvals-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={approvingId === c.session_id}
                      onClick={() => handleApprove(c.session_id)}
                    >
                      {approvingId === c.session_id ? <span className="spinner spinner-sm" /> : '✓ Genehmigen'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm pending-reject-btn"
                      onClick={() => { setRejectModal({ sessionId: c.session_id, visitorName: `${c.first_name} ${c.last_name}` }); setRejectReason('') }}
                    >
                      ✕ Ablehnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <span className="spinner" />
            <span>Wird geladen...</span>
          </div>
        ) : checkins.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>Keine Check-ins gefunden{filterDate ? ` für ${filterDate}` : ''}</p>
          </div>
        ) : displayCheckins.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p>Keine Einträge entsprechen dem Filter.</p>
          </div>
        ) : (
          <div className="table-wrap checkins-table-wrap">
            <table>
              <colgroup>
                {visibleCols.map(col => (
                  <col key={col.key} style={{ width: colWidths[col.key] }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {visibleCols.map(col => (
                    <th
                      key={col.key}
                      style={{ width: colWidths[col.key] }}
                      className={SORTABLE_COLS.has(col.key) ? 'th-sortable' : undefined}
                      onClick={SORTABLE_COLS.has(col.key) ? () => handleSort(col.key) : undefined}
                    >
                      {col.label}
                      {SORTABLE_COLS.has(col.key) && (
                        <span className={`sort-icon${sortKey === col.key ? ' sort-icon--active' : ''}`}>
                          {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      )}
                      <div
                        className="col-resize-handle"
                        onMouseDown={e => onResizeStart(e, col.key)}
                        onClick={e => e.stopPropagation()}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayCheckins.map(c => (
                  <tr key={c.session_id}>
                    {visibleCols.map(col => (
                      <td key={col.key}>{renderCell(col.key, c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="checkins-count">
        {checkins.length > 0 && (
          displayCheckins.length === checkins.length
            ? `${checkins.length} Einträge`
            : `${displayCheckins.length} von ${checkins.length} Einträgen`
        )}
      </div>

      {/* QR modal */}
      {qrModal && (
        <div className="checkins-modal-overlay" onClick={() => setQrModal(null)}>
          <div className="checkins-qr-modal" onClick={e => e.stopPropagation()}>
            <button className="checkins-modal-close" onClick={() => setQrModal(null)}>✕</button>
            <div className="checkins-qr-label">QR-Code für</div>
            <div className="checkins-qr-name">{qrModal.visitorName}</div>
            <img src={qrModal.qrBase64} alt="QR Code" className="checkins-qr-img" />
            <div className="checkins-qr-string">{qrModal.qrString}</div>
            <button
              className="btn btn-primary checkins-qr-download"
              onClick={() => handlePrintQr(qrModal)}
            >
              🖨 Drucken
            </button>
            <button
              className="btn btn-ghost checkins-qr-download"
              onClick={() => {
                const a = document.createElement('a')
                a.href = qrModal.qrBase64
                a.download = `QR-${qrModal.visitorName.replace(/\s+/g, '-')}.png`
                a.click()
              }}
            >
              ⬇ Herunterladen
            </button>
          </div>
        </div>
      )}

      {/* Rejection reason modal */}
      {rejectModal && (
        <div className="checkins-modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="checkins-sms-modal" onClick={e => e.stopPropagation()}>
            <button className="checkins-modal-close" onClick={() => setRejectModal(null)}>✕</button>
            <div className="checkins-qr-label">Ablehnen</div>
            <div className="checkins-qr-name">{rejectModal.visitorName}</div>
            <div className="form-group" style={{ marginTop: 8 }}>
              <label>Ablehnungsgrund (optional)</label>
              <textarea
                className="form-control checkins-sms-textarea"
                rows={3}
                placeholder="z. B. Kein Termin vereinbart"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
            </div>
            <div className="checkins-sms-actions">
              <button className="btn btn-ghost" onClick={() => setRejectModal(null)}>Abbrechen</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={rejectSaving}>
                {rejectSaving ? <><span className="spinner spinner-sm" /> Ablehnen…</> : '✕ Ablehnen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS modal */}
      {smsModal && (
        <div className="checkins-modal-overlay" onClick={() => setSmsModal(null)}>
          <div className="checkins-sms-modal" onClick={e => e.stopPropagation()}>
            <button className="checkins-modal-close" onClick={() => setSmsModal(null)}>✕</button>
            <div className="checkins-qr-label">SMS senden an</div>
            <div className="checkins-qr-name">{smsModal.visitorName}</div>
            <div className="checkins-sms-phone">{smsModal.phone}</div>

            <div className="checkins-sms-presets">
              {SMS_PRESETS.map(p => (
                <button
                  key={p}
                  className={`checkins-sms-preset${smsMessage === p ? ' active' : ''}`}
                  onClick={() => setSmsMessage(p)}
                >
                  {p}
                </button>
              ))}
            </div>

            <textarea
              className="form-control checkins-sms-textarea"
              rows={3}
              placeholder="Eigene Nachricht eingeben…"
              value={smsMessage}
              onChange={e => setSmsMessage(e.target.value)}
            />

            {smsResult && (
              <div className={`alert ${smsResult.ok ? 'alert-success' : 'alert-error'}`}>
                {smsResult.text}
              </div>
            )}

            <div className="checkins-sms-actions">
              <button className="btn btn-ghost" onClick={() => setSmsModal(null)}>Abbrechen</button>
              <button
                className="btn btn-primary"
                onClick={handleSendSms}
                disabled={smsSending || !smsMessage.trim()}
              >
                {smsSending ? <><span className="spinner spinner-sm" /> Senden…</> : '💬 SMS senden'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document lightbox */}
      {viewingDocument && (
        <div className="checkins-doc-overlay" onClick={closeDocument}>
          <div className="checkins-doc-content" onClick={e => e.stopPropagation()}>
            <button className="checkins-doc-close" onClick={closeDocument}>✕</button>
            <img src={viewingDocument.url} alt={viewingDocument.name} className="checkins-doc-img" />
            <a href={viewingDocument.url} download={viewingDocument.name} className="checkins-doc-link">
              ⬇ Herunterladen
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

export default Checkins
